/**
 * Transfer between legal entities: the vocabulary both sides share.
 *
 * A transfer ends one Employment and starts another for the SAME Person (one login, one history).
 * These constants and pure helpers are shared so the API's refusals, the preview screen's blocker
 * list and the wizard's date rules cannot drift apart. Plan: docs/plans/legal-entity-transfer/.
 */

/**
 * The exit type stamped on the employment a transfer ends. A RESERVED code, not `transfer`: an
 * organisation may already have made its own exit type called `transfer` and used it, and that one
 * must stay an ordinary reason. The row is created lazily (first transfer, and for new organisations
 * by `provisionExitTypeDefaults`); it is never offered in the resignation or exit forms and never
 * counted as a leaver.
 */
export const TRANSFER_EXIT_TYPE = 'system_transfer';

/** Display name of the system exit type. */
export const TRANSFER_EXIT_TYPE_NAME = 'Transfer';

/** Codes only the system may create: an admin cannot make an exit type with one of these. */
export const RESERVED_EXIT_TYPE_CODES: readonly string[] = [TRANSFER_EXIT_TYPE];

/** True for an exit type that is a system value and so is not offered in any exit form. */
export function isSystemExitType(code: string | null | undefined): boolean {
  return code === TRANSFER_EXIT_TYPE;
}

/** True when `code` may not be created, renamed to or edited by an administrator. */
export function isReservedExitTypeCode(code: string): boolean {
  return RESERVED_EXIT_TYPE_CODES.includes(code.trim().toLowerCase());
}

/**
 * Why a transfer is refused. The same codes come back as `details.reason` on a 409/422 and as the
 * `code` of each blocker the preview lists, so the screen can disable "Confirm" before the click.
 */
export const TRANSFER_BLOCKER_CODES = [
  /** The new company is the one they are already in. */
  'same_company',
  /** The new company is switched off. */
  'company_inactive',
  /** The date is before the person joined the old company. */
  'before_joined',
  /** Either company's payroll is already finalized on or after the date. */
  'payroll_finalized',
  /** A payroll run covering the date has locked its inputs but is not finalized yet. */
  'payroll_in_progress',
  /** The person has already exited, been rescinded, or is serving notice or resigning. */
  'not_transferable_status',
  /** An exit is started, or a full & final settlement is open. */
  'exit_in_progress',
  /** The employee was deleted (archived) from the list. */
  'archived',
  /** A transfer is already waiting for its date; one at a time. */
  'transfer_pending',
  /** The chosen location belongs to a third company. */
  'location_other_company',
  /** Grade, cost centre or business unit does not exist in the new company. */
  'placement_invalid',
  /** The new company has no ID series and no employee code was typed. */
  'no_employee_code',
  /** The typed employee code is already used in the organisation. */
  'code_taken',
  /** More than one employment of the person is in effect (a data problem). */
  'multiple_current_employments',
  /** The new manager is the person themselves or reports to them. */
  'manager_loop',
] as const;
export type TransferBlockerCode = (typeof TRANSFER_BLOCKER_CODES)[number];

/** Refusals that are about the request's choices, not the state of things (they are HTTP 422). */
export const TRANSFER_VALIDATION_CODES = [
  /** The pay currency changes and no salary was entered in the new one. */
  'salary_required',
  /** Future salary revisions cannot be carried across a currency change. */
  'revisions_currency',
  /** Open claims, advances, loans or leave requests were not marked as reviewed. */
  'open_items_not_reviewed',
] as const;
export type TransferValidationCode = (typeof TRANSFER_VALIDATION_CODES)[number];

/**
 * A transfer that starts on any day but the 1st splits that month's pay between two companies.
 * The wizard defaults to the 1st of a month and shows this as a warning, not an error.
 */
export function isMidMonth(effectiveOn: string): boolean {
  return effectiveOn.slice(8, 10) !== '01';
}

/** The first day of the NEXT month after `today` (the wizard's default effective date). */
export function nextMonthStart(today: string): string {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${String(nextYear).padStart(4, '0')}-${String(nextMonth).padStart(2, '0')}-01`;
}

/**
 * Whether a transfer dated `effectiveOn` is still waiting: it has not gone live in the new company.
 * `today` is the person's own day (their location's zone), never the server's.
 */
export function isTransferPending(effectiveOn: string, today: string): boolean {
  return effectiveOn > today;
}

/**
 * Statuses a person may be transferred from. Their status carries over unchanged: a probation stays
 * a probation (same end date), a confirmed person stays confirmed, someone on long leave stays on
 * leave. Not here: `pre_joining` (has not joined yet), `notice_period` (serving notice),
 * `exited` and `rescinded`.
 */
export const TRANSFERABLE_STATUSES = ['probation', 'confirmed', 'on_leave'] as const;
export type TransferableStatus = (typeof TRANSFERABLE_STATUSES)[number];

export function isTransferableStatus(status: string): status is TransferableStatus {
  return (TRANSFERABLE_STATUSES as readonly string[]).includes(status);
}
